/**
 * External dependencies.
 */
import type { GetStaticProps, NextPage } from "next";
import { Trans, t } from "@lingui/macro";
import classNames from "classnames";

/**
 * Internal dependencies.
 */
import {
  fetchAPI,
  getLocale,
  getLocalizationData,
  loadTranslation,
} from "utils";
import Layout from "../../layout/layout";
import TealBox from "components/teal-box/teal-box";
import FormTopicSearch from "blocks/form-topic-search/form-topic-search";
import QuickHelp from "components/quick-help/quick-help";
import BtnLink from "components/btn-link/btn-link";
import Link from "components/link/link";
import SchemaSet from "components/schema-set/schema-set";
import Breadcrumbs from "blocks/breadcrumbs/breadcrumbs";
import SEO from "components/seo/seo";

const HelpCenter: NextPage = (prop: any) => {
  const locale = prop?.locale;
  const {
    QuickActions,
    FAQLink,
    BrowseAll,
    SEO: seoData,
    title,
  } = prop?.data?.attributes ?? {};

  return (
    <Layout title="Themes" desc="Themes" light notice>
      <SEO data={seoData} />
      <SchemaSet
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Themes",
        }}
      />
      <TealBox center maxWidth="544px">
        <h1 className="h3 ta-c">{title}</h1>
        <FormTopicSearch locale={locale} />
      </TealBox>
      <div className="container container--small">
        <Breadcrumbs />
        <QuickHelp items={QuickActions} title={t`Quick Actions`} horizontal />
        <div className="divider"></div>
        <div className="common-list">
          <div className="common-list__head">
            <h6>
              <Trans>Common FAQs</Trans>
            </h6>
            <BtnLink href={BrowseAll} title={t`Browse all`}>
              <Trans>Browse all</Trans>
            </BtnLink>
          </div>
          <ul className="common-list__items">
            {FAQLink?.map((link: any, index: number) => (
              <li
                key={`link-${link.id}`}
                className={classNames({
                  "no-border": FAQLink.length - 2 <= index,
                })}
              >
                <Link href={link.URL}>{link.Text}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const { data } = await fetchAPI("/help-centre-landing", {
    populate: {
      QuickActions: {
        populate: {
          Icon: "*",
        },
      },
      SEO: {
        popualte: "*",
      },
      FAQLink: "*",
      localizations: "*",
    },
    locale,
  });

  return {
    props: {
      translation,
      data,
      locale: ctx.locale,
      localizationData: getLocalizationData(data, "help-center"),
    },
    revalidate: 10,
  };
};

export default HelpCenter;
